import React from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import useDbTypeColors from './useDbTypeColors';
import { useDbColumnResize } from '../useDbColumnResize';
import {
  parseCellKey,
  ROW_HEIGHT_ESTIMATE,
  MAX_RENDER_ROWS,
  VIRTUALIZE_MIN_ROWS,
  ROWNUM_WIDTH_KEY,
} from '../dbGridModel';
import { measureColumnWidths } from './dbColumnWidths';
import { observeOffsetFromElement, PINNED_WIDTHS } from './resultsGridLayout';

/** Scroll host and anchor cell, scroll restore, row virtualisation, find-arrival scrolling, and pinned / resizable column widths for the results grid. */
export function useGridViewport({
  result,
  loading,
  error,
  pendingRows,
  editingCell,
  selectedRows,
  selectedCells,
  columnTypes,
  columnWidths,
  onColumnResize,
  activeFindKey,
  cursorKey,
  hiddenColumns,
  onScrollNearEnd,
  onScrollPosition,
  initialScrollTop,
  scrollRestoreKey,
  anchorToSelection,
}) {
  const scrollHostRef = React.useRef(null);
  // Where the grid was last seen scrolled to — what a REPLACED scroll element is
  // put back to (see the restore effect).
  const lastScrollTopRef = React.useRef(0);

  /**
   * The element that actually scrolls, once it exists.
   *
   * Declared first because three separate things wait on it — the scroll
   * listener below, the scroll restore, and the virtualizer — and every one of
   * them used to resolve it independently and give up if it was not there yet.
   *
   * OsScroll passes `defer`, so OverlayScrollbars initialises lazily and
   * `getElement()` can still answer null on the first effect pass; it cannot be
   * read during render either way. Hence the retry. One look was enough for a
   * tab switch, where this component stays MOUNTED and its effects re-run as
   * props change, but a record-view flip swaps which grid is mounted, so the
   * horizontal grid comes back FRESH and gets a single look — and nothing in
   * those effects' deps changes after a mount to bring them back.
   *
   * Bounded rather than open-ended: in the loading/error branches there is no
   * OsScroll to find at all, and an unbounded rAF chain there would spin every
   * frame until a result arrived. The deps re-arm it when that happens.
   */
  const [scrollViewport, setScrollViewport] = React.useState(null);
  React.useEffect(() => {
    let frame = 0;
    let attempts = 0;
    let cancelled = false;
    const find = () => {
      if (cancelled) return;
      const host = scrollHostRef.current?.getElement?.() || null;
      const real = host?.querySelector('[data-overlayscrollbars-viewport]') || null;
      const vp = real || host || null;
      if (vp) setScrollViewport((prev) => (prev === vp ? prev : vp));
      // THE HOST IS ONLY AN INTERIM ANSWER. Until OverlayScrollbars initialises
      // (deferred, after paint) the wrapper itself is what scrolls; afterwards a
      // viewport INSIDE it does, and the wrapper never scrolls again. This used
      // to stop at the first answer, so the switch to the real element happened
      // whenever something else re-ran the effect — in practice when the next
      // chunk of rows arrived, i.e. in the middle of a scroll. Everything bound
      // to `scrollViewport` (the virtualizer, the restore) then re-bound to an
      // element it knew nothing about. Keep looking until the real one exists,
      // so the hand-over happens within a frame of it appearing.
      if (real) return;
      if (++attempts > 60) return; // ~1s; it is not coming (jsdom, no OverlayScrollbars)
      frame = requestAnimationFrame(find);
    };
    find();
    return () => {
      cancelled = true;
      if (frame) cancelAnimationFrame(frame);
    };
  }, [loading, error, result]);

  /**
   * Ask for more rows when the scroll nears the bottom.
   *
   * Listens in the CAPTURE phase, which is the whole trick. The element that
   * actually scrolls is a viewport OverlayScrollbars renders inside this one,
   * and scroll events do not bubble — so a handler on the wrapper never sees
   * them. Capture fires on ancestors on the way DOWN to the target, which
   * non-bubbling events still do, so this catches whichever descendant scrolls
   * without needing to know the library's DOM or reach into its instance API.
   *
   * (The first version used React's onScroll on the wrapper. React attaches
   * non-bubbling events directly to the element carrying the prop, so it never
   * fired in a browser — and the jsdom test passed only because OverlayScrollbars
   * renders no viewport there, leaving the wrapper itself as the target.)
   */
  React.useEffect(() => {
    if (!onScrollNearEnd && !onScrollPosition) return undefined;
    const host = scrollHostRef.current?.getElement?.() || null;
    if (!host) return undefined;

    const onScroll = (e) => {
      const vp = e.target;
      if (!vp || typeof vp.scrollHeight !== 'number') return;
      // Reported on every scroll rather than debounced: it is a number
      // assignment, and a debounce would lose the last position when the tab
      // is switched immediately after scrolling — which is exactly when it
      // matters.
      // ONLY FROM THE ELEMENT THE POSITION WAS RESTORED INTO. When OverlayScrollbars
      // swaps the scroll element, the fresh viewport announces itself with a scroll
      // event at 0 (and the retired wrapper can emit one too) before the restore
      // has carried the position over. Believing that event overwrote the very
      // number the restore was about to use — it then "restored" to 0 — and it
      // also reached the tab's saved position, so the NEXT visit started at the
      // top as well. An element nothing has been restored into yet has no
      // position worth remembering.
      const restoredInto = restoredScrollFor.current?.el;
      if (restoredInto && vp !== restoredInto) return;
      // …and never from the wrapper once a real viewport lives inside it: from
      // then on the wrapper does not scroll, and the one event it still emits is
      // OverlayScrollbars zeroing it on the way in.
      if (vp === host && host.querySelector('[data-overlayscrollbars-viewport]')) return;
      lastScrollTopRef.current = vp.scrollTop;
      if (onScrollPosition) onScrollPosition(vp.scrollTop);
      if (!onScrollNearEnd) return;
      // A screenful of slack, so the next chunk is usually already there by
      // the time the scroll actually reaches the bottom.
      const remaining = vp.scrollHeight - vp.scrollTop - vp.clientHeight;
      if (remaining < vp.clientHeight) onScrollNearEnd();
    };

    host.addEventListener('scroll', onScroll, true);
    return () => host.removeEventListener('scroll', onScroll, true);
    // loading/error/result are dependencies because this component returns
    // early while loading — the scroll host does not exist on the first render,
    // so an effect that ran only once would find a null ref and never attach.
    //
    // `scrollViewport` is a dependency for the same reason, one level deeper,
    // and it is what makes repeated record-view flips work. Those deps only
    // re-arm this while the component STAYS mounted; a flip remounts the grid
    // with a result already in hand, so none of them change and this got a
    // single attempt against a viewport OsScroll had not built yet. The
    // listener then never attached, `scrollTopRef` stopped being updated, and
    // the NEXT flip captured a stale position — which is why one round trip
    // worked and the ones after it restored somewhere the user had long since
    // scrolled away from. `scrollViewport` is resolved with a retry, so it
    // becomes non-null exactly when there is something to attach to.
  }, [onScrollNearEnd, onScrollPosition, loading, error, result, scrollViewport]);

  /**
   * Put a restored tab back where it was left.
   *
   * Runs once per tab, not on every render — re-applying it would fight the
   * user the moment they scrolled, and `result` is in the deps because chunks
   * keep arriving. The rows have to be laid out first for the target to be
   * reachable at all (an unlaid-out viewport clamps scrollTop to zero), which
   * is what the animation frame is for.
   *
   * SCROLLING TO 0 IS A RESTORE, not a no-op. This used to bail on
   * `!initialScrollTop`, on the reading that "no saved position" means "nothing
   * to do". It does not, because switching between two DB Manager tabs does not
   * REMOUNT this component — both are `db-manager-table`, so EditorPane returns
   * the same element and the viewport keeps the previous tab's scrollTop. A tab
   * saved at the top therefore arrived showing the previous tab's offset.
   *
   * Worse, that early return skipped the `restoredScrollFor` write too, so the
   * ref went stale: coming BACK to the first tab then matched the guard and was
   * skipped entirely, leaving it wherever the other tab had left the shared
   * viewport. That is the "first switch holds the position, the next one resets
   * to the top" report — one viewport, two tabs, and a guard that had lost
   * track of which one it was holding.
   *
   * It waits for `scrollViewport` instead of resolving the element itself, and
   * that is the difference between a tab switch and a record-view flip. Both
   * end with this grid needing to scroll, but a flip REMOUNTS it, and on a
   * fresh mount the deferred OverlayScrollbars viewport is not there yet. The
   * old version looked once, found nothing, and returned — with no dep that
   * would ever bring it back, because `initialScrollTop`/`scrollRestoreKey`/
   * `result` are all unchanged after a mount. A tab switch survived that only
   * because it does NOT remount: those props kept changing, so the effect got
   * more attempts. Depending on the viewport gives the flip its attempt too.
   */
  /**
   * The row the view is "on", if anything is selected or being edited.
   *
   * This is what a record-view flip should come back to, and it is a far
   * better anchor than a remembered pixel offset. A saved offset has to survive
   * three things lining up on a fresh mount — the number still being accurate,
   * the scroll element existing, and the content being tall enough at the
   * instant it is applied — and the flip breaks all three at once, because it
   * REMOUNTS the grid next to a lazily-initialised scrollbar and a virtualizer
   * that changes the content height a frame later. Each of those was fixed
   * separately and the flip still landed wrong.
   *
   * A row index needs none of it. The selection already survives the flip
   * untouched, so the answer to "where was I" is carried in state rather than
   * reconstructed from geometry.
   *
   * Precedence: the cell being edited, then a selected cell, then a selected
   * row — most specific first, because that is the thing the user was looking
   * at. `null` when nothing is selected, and the pixel offset takes over —
   * which is the right answer for arriving from another tab with no selection.
   */
  const anchorCell = React.useMemo(() => {
    // Only a record-view flip anchors to the selection. A tab arrival restores
    // its saved offset instead — you left that tab looking at a place, and a
    // selection made earlier should not move you. The caller decides, because
    // both arrive here as "restore something" and this component cannot tell
    // them apart.
    if (!anchorToSelection) return null;
    // The COLUMN matters as much as the row. `scrollIntoView` on a `<tr>` only
    // ever moves the vertical axis — the row spans the full width, so it is
    // already "in view" horizontally however far right the selected column
    // sits. Carrying the column lets the restore scroll the actual cell, which
    // moves both scrollbars.
    if (editingCell && Number.isInteger(editingCell.rowIndex)) {
      return { row: editingCell.rowIndex, col: editingCell.colIndex ?? null };
    }
    if (selectedCells?.size) {
      for (const key of selectedCells) {
        const { kind, id, col } = parseCellKey(key);
        if (kind === 'r' && Number.isInteger(id)) return { row: id, col: Number.isInteger(col) ? col : null };
      }
    }
    // A row selection has no column to aim at, so this stays vertical-only.
    if (selectedRows?.size) return { row: Math.min(...selectedRows), col: null };
    return null;
  }, [anchorToSelection, editingCell, selectedCells, selectedRows]);

  /**
   * Focus the anchor cell on arrival, the way `autoFocus` focuses the editor.
   *
   * This is the mid-edit flip's mechanism, not an imitation of its result. That
   * path renders `<input autoFocus>`; React focuses it during commit, the
   * browser scrolls it into view, and — the part that matters under `defer` —
   * focus survives OverlayScrollbars reparenting the table after paint, which
   * a scroll offset does not.
   *
   * Only when nothing is being edited: the editor's own `autoFocus` owns focus
   * on that path, and two claims on it in one commit is a race.
   *
   * Once per mount. The anchor means "where I came in", so re-focusing when the
   * selection later moves would fight the user's own clicks.
   */
  const focusedAnchor = React.useRef(false);
  const focusAnchorCell = React.useCallback((el) => {
    if (!el || focusedAnchor.current) return;
    focusedAnchor.current = true;
    el.focus();
  }, []);

  /**
   * NOTHING HERE MAY SCROLL BEFORE PAINT. It cannot work, and it breaks what does.
   *
   * `OsScroll` passes `defer`, so OverlayScrollbars initialises on an idle
   * callback — after paint — and when it does it reparents this table into a
   * fresh `[data-overlayscrollbars-viewport]` that starts at scrollTop 0. A
   * layout effect therefore runs while the only scrollable thing is the
   * pre-init `.dbm-grid-wrap`, and anything it scrolls is discarded moments
   * later. That is why the effect below waits for `scrollViewport` rather than
   * resolving an element itself.
   *
   * A `useLayoutEffect` that scrolled here was tried, to kill the one painted
   * frame at the top of the table. It did not remove the frame (the scroll was
   * thrown away), it scrolled outer ancestors on the way past — `scrollIntoView`
   * moves EVERY scrollable ancestor, not just the one you meant — and because
   * it marked the restore done it suppressed this effect, so the flip stopped
   * restoring anything at all. It regressed the mid-edit flip, which had always
   * worked, into the broken case it was meant to fix.
   *
   * The frame at the top is the price of `defer`. Pay it here; the caret is
   * carried by focus, which does survive the reparent — see the anchor cell's
   * `focusAnchorCell` ref.
   */
  const restoredScrollFor = React.useRef(null);

  React.useEffect(() => {
    if (!scrollViewport) return undefined;
    const done = restoredScrollFor.current;
    if (done && done.key === scrollRestoreKey && done.el === scrollViewport) return undefined;

    // "Restored" means restored INTO THIS ELEMENT. The guard used to remember the
    // tab key alone, so a position put into the interim wrapper counted as done —
    // and when OverlayScrollbars replaced it with a fresh viewport at scrollTop 0,
    // nothing put it back: leaving the DB Manager at row 1,400 and returning
    // landed on row 8. Same tab, NEW element → carry the position over.
    //
    // WHICH position depends on what was replaced. The interim wrapper lives for
    // a few frames and nobody scrolls it in that time — but OverlayScrollbars
    // zeroes it while taking over, so its last reading is 0, not the truth. What
    // it was AIMED at is the truth. A real viewport being replaced by another
    // (it happens when the result is rebuilt) does carry a position the user
    // chose, so there the last reading wins.
    const sameTabNewElement = !!done && done.key === scrollRestoreKey;
    const target = !sameTabNewElement
      ? initialScrollTop || 0
      : done.interim
        ? done.target
        : lastScrollTopRef.current;
    restoredScrollFor.current = {
      key: scrollRestoreKey,
      el: scrollViewport,
      target,
      interim: !scrollViewport.hasAttribute('data-overlayscrollbars-viewport'),
    };
    lastScrollTopRef.current = target;
    const frame = requestAnimationFrame(() => {
      if (anchorCell) {
        // Addressed by data attributes, NOT by position in the node list.
        //
        // The obvious version branched on `windowed` — Nth `<tr>` when every
        // row is drawn, `scrollToIndex` when only a window is. That is a race:
        // `windowed` is captured when the effect runs, the frame fires later,
        // and the render in between is exactly the one where the virtualizer
        // finishes measuring and the tbody goes from every row to ~30. The
        // stale `false` then indexes row 300 into a 30-element list, finds
        // nothing, and scrolls nowhere.
        //
        // The attributes carry ABSOLUTE indexes in both modes, so this asks
        // the DOM a question that stays true either way: is that row drawn? If
        // it is, scroll it into view; if it is not, only the virtualizer knows
        // where it lives.
        const row = scrollViewport.querySelector(
          `tbody tr.dbm-tr[data-row-index="${anchorCell.row}"]`
        );
        if (row) {
          // The CELL when we know which column, because scrolling the `<tr>`
          // only ever moves the vertical axis — a row spans the full width, so
          // it is already in view horizontally however far right the selected
          // column is. `inline: 'nearest'` brings that column into view
          // without yanking the horizontal position when it is already visible.
          const cell =
            anchorCell.col != null
              ? row.querySelector(`td[data-col-index="${anchorCell.col}"]`)
              : null;
          (cell || row).scrollIntoView({ block: 'center', inline: 'nearest' });
          return;
        }
        virtualizer.scrollToIndex(anchorCell.row, { align: 'center' });
        // The row was not drawn, so its cell could not be either. Once the
        // virtualizer has put it on screen, take the horizontal axis on the
        // next frame — the same two-step the find-match scroll uses.
        if (anchorCell.col != null) {
          requestAnimationFrame(() => {
            scrollViewport
              .querySelector(
                `tbody tr.dbm-tr[data-row-index="${anchorCell.row}"] td[data-col-index="${anchorCell.col}"]`
              )
              ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          });
        }
        return;
      }
      scrollViewport.scrollTop = target;
    });
    return () => cancelAnimationFrame(frame);
    // `virtualizer` is read inside the frame callback, which runs long after
    // this render finished, so the later declaration is safe. It is NOT a dep:
    // it is a new object every render, and re-running this would fight the
    // user's own scrolling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialScrollTop, scrollRestoreKey, result, scrollViewport, anchorCell]);

  /**
   * Row virtualization (spacer-row technique).
   *
   * The grid used to put every row it drew into the DOM — up to
   * MAX_RENDER_ROWS, i.e. 10,000 `<tr>`s × N `<td>`s. Because EditorPane
   * unmounts this component when you switch to Collections and mounts a fresh
   * one when you come back, that whole tree was rebuilt on every tab switch,
   * which is the stall this fixes. The rows themselves never went anywhere —
   * they sit in the in-memory dbQueryResultCache — so nothing here is about
   * loading, only about how much DOM the return trip has to construct.
   *
   * SPACERS, not `transform: translateY`. This is a real `<table>` with a
   * `<colgroup>`, `table-layout: fixed` and a sticky `<thead>`, and transforms
   * fight the browser's table-layout maths and break sticky positioning. Two
   * empty `<tr>`s carrying the height of the rows above and below keep every
   * one of those properties native, so column resize, sticky headers and the
   * scroll-geometry maths below all continue to work untouched.
   *
   * Row height is measured once rather than per row — see `rowHeight` below.
   */

  /**
   * One row's real height, measured ONCE per mount off a rendered row.
   *
   * Not `measureElement`: that measures every row as it enters the window, so
   * scrolling pays a layout read per row per frame — a forced synchronous
   * reflow in the middle of the scroll, which is exactly the jank virtualizing
   * was supposed to remove. Rows here are uniform by CSS (`height: 36px`,
   * `nowrap`, `overflow: hidden`, so a cell can never wrap), so one measurement
   * describes all of them. The `>= 1` guard stops a sub-pixel difference from
   * ping-ponging this state forever.
   */
  const [rowHeight, setRowHeight] = React.useState(ROW_HEIGHT_ESTIMATE);
  React.useEffect(() => {
    const host = scrollHostRef.current?.getElement?.();
    const measured = host?.querySelector('tbody tr.dbm-tr')?.getBoundingClientRect?.().height;
    // 0 is jsdom (no layout engine) and a pre-layout first paint; keep the
    // constant rather than collapsing every row to nothing.
    if (measured && Math.abs(measured - rowHeight) >= 1) setRowHeight(measured);
  }, [result, rowHeight]);

  // Derived from `result` rather than from `rowsToRender`, which is computed
  // below the loading/error early-returns — hooks have to run before those, so
  // the count cannot depend on anything declared after them. Same value: the
  // row cap is the cap either way.
  const virtualizableRows = Math.min(result?.rows?.length ?? 0, MAX_RENDER_ROWS);
  const virtualizer = useVirtualizer({
    // Under the threshold this is 0, which makes the virtualizer inert: it
    // yields no items, `windowed` stays false, and the grid renders every row
    // exactly as it did before any of this existed. Small tables get their
    // React-free scrolling back.
    count: virtualizableRows >= VIRTUALIZE_MIN_ROWS ? virtualizableRows : 0,
    getScrollElement: () => scrollViewport,
    estimateSize: () => rowHeight,
    // A screenful either side, so ordinary wheel scrolling never reaches an
    // unrendered row before the next frame lands.
    overscan: 16,
    observeElementOffset: observeOffsetFromElement,
  });
  const virtualRows = virtualizer.getVirtualItems();

  /**
   * Windowing is used ONLY when the virtualizer actually produced a window.
   *
   * Zero items means it could not measure the viewport — no scroll element yet
   * on first paint, or a layout engine that reports every element as zero-high,
   * which is exactly jsdom. Rendering everything in that case is the honest
   * degradation in both settings: the first paint is never blank, and the test
   * suite keeps seeing the same DOM it always did. A virtualizer that silently
   * renders nothing when it cannot measure would be the worst of both.
   */
  const windowed = virtualRows.length > 0;
  const padTop = windowed ? virtualRows[0].start : 0;
  /**
   * The bottom spacer, less whatever the inline pending rows added.
   *
   * The virtualizer sizes the scroll range as `count × rowHeight` and knows
   * nothing about rows drawn between them. A duplicate rendered under its
   * source adds a row of real height inside the window, so without this the
   * content would be taller than the range the virtualizer is mapping scroll
   * offsets onto, and every row below would sit lower than it thinks.
   *
   * Only rows whose source is DRAWN are rendered at all, so there is never
   * unaccounted height above the window — that side needs no correction.
   * Rows below the insertion but still inside the window do shift down by a row
   * height, which is the point; the residual is that scrolling while a
   * duplicate is on screen can step by that much. Staged duplicates are a
   * transient state measured in seconds, and the alternative — teaching the
   * virtualizer variable row sizes — reintroduces the per-row measurement this
   * grid deliberately does not do.
   *
   * COUPLING: this counts pending rows and multiplies by `rowHeight`, which was
   * measured off a SAVED row. That holds because a pending row carries the same
   * `dbm-tr` class and therefore the same `height: 36px`. If `.dbm-pending-tr`
   * ever grows its own height, this correction is wrong by the difference and
   * the virtualizer's offset model drifts — which shows up as scroll jitter,
   * not as an error. `dbGridVirtualization.test.js` asserts the two heights
   * agree so the coupling fails loudly instead.
   */
  const inlinePendingCount = windowed
    ? (pendingRows || []).filter(
        (p) =>
          Number.isInteger(p.afterRow) &&
          p.afterRow >= virtualRows[0].index &&
          p.afterRow <= virtualRows[virtualRows.length - 1].index
      ).length
    : 0;
  const padBottom = windowed
    ? Math.max(
        0,
        virtualizer.getTotalSize() -
          virtualRows[virtualRows.length - 1].end -
          inlinePendingCount * rowHeight
      )
    : 0;

  /**
   * Bring the active find match into view.
   *
   * This lived in DbManagerResultsTab as a bare
   * `document.querySelector('.dbm-td--find-active')` + `scrollIntoView`, which
   * silently stopped working the moment a match sat outside the rendered
   * window: querySelector returns null, the optional call swallows it, and the
   * "n of m" counter keeps advancing while the viewport does not move. It has
   * to be here because only this component knows where row N is.
   *
   * Two steps, and both are needed. `scrollToIndex` puts the ROW on screen
   * (the virtualizer knows its offset whether or not it is currently drawn);
   * the deferred querySelector then scrolls the CELL horizontally, which is
   * what matters on a wide table — and by that frame the row it belongs to has
   * been rendered. With windowing off, step one is a no-op and this behaves
   * exactly as the old code did.
   *
   * It does NOT fire for the match you arrive already sitting on. A fresh mount
   * runs every effect, so with the find bar open a record-view flip had two
   * things scrolling the new grid at once — the anchor restore heading for the
   * selected cell and this heading for the active match — which is the up-down
   * lurch that only happened with Ctrl+F open. Stepping to a different match is
   * a deliberate "take me there" and still scrolls; the arrival is not, and
   * belongs to whoever restored the position.
   *
   * The latch releases permanently on the first real step, so returning to the
   * arrival match later still scrolls to it.
   */
  const arrivalFindKey = React.useRef(undefined);
  React.useEffect(() => {
    if (arrivalFindKey.current === undefined) arrivalFindKey.current = activeFindKey;
    if (activeFindKey && activeFindKey === arrivalFindKey.current) return undefined;
    arrivalFindKey.current = null;
    if (!activeFindKey) return undefined;
    const parsed = parseCellKey(activeFindKey);
    if (windowed && parsed?.kind === 'r' && Number.isInteger(parsed.id)) {
      virtualizer.scrollToIndex(parsed.id, { align: 'auto' });
    }
    const frame = requestAnimationFrame(() => {
      document
        .querySelector('.dbm-td--find-active')
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
    // `virtualizer` is intentionally absent: it is a new object every render,
    // and re-running this on every render would fight the user's own scrolling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFindKey, windowed]);

  // Keep the keyboard's cell on screen (dbManager/useDbGridKeyboardNav.js):
  // the arrow keys used to scroll the grid instead of moving the cell.
  React.useEffect(() => {
    if (!cursorKey) return undefined;
    const parsed = parseCellKey(cursorKey);
    if (windowed && parsed?.kind === 'r' && Number.isInteger(parsed.id)) {
      virtualizer.scrollToIndex(parsed.id, { align: 'auto' });
    }
    const frame = requestAnimationFrame(() => {
      document
        .querySelector(`[data-cell-key="${cursorKey}"]`)
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
    // `virtualizer` is a new object every render (see the find effect above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursorKey, windowed]);

  const hasColumnWidths = Object.keys(columnWidths).length > 0;

  // Pin the widths once the first rows have painted, so scrolling (which swaps
  // the rows the auto layout measures) can no longer make columns jump.
  //
  // Written straight onto the header cells, with no React state and no tab
  // write: both cost a re-render right after load, which raced the scroll
  // restore and the load-more wiring. Under `table-layout: fixed` the first
  // row's cell widths define the columns; persisted widths (a manual resize,
  // the <colgroup>) take precedence over them, and a drag seeds from these.
  // Keyed by tab AND visible column set: load-more, sort and filter keep them;
  // a new column set is measured afresh; and each tab keeps its own, across
  // tab switches AND the record view (which unmounts this grid) — a tab used to
  // come back re-measured from whatever rows it held then, e.g. two filtered
  // rows (owner, 06.10.2026: "at least remember it"). See PINNED_WIDTHS.
  const tableElRef = React.useRef(null);
  const pinsRef = React.useRef(PINNED_WIDTHS);
  const visibleSignature = (result?.columns || []).filter((c) => !hiddenColumns.has(c)).join('\u0001');
  const pinKey = `${scrollRestoreKey ?? ''}\u0002${visibleSignature}`;
  React.useLayoutEffect(() => {
    const table = tableElRef.current;
    if (hasColumnWidths || loading || !result?.rows?.length || !table) return;
    const visible = (result.columns || []).filter((c) => !hiddenColumns.has(c));
    const pinned = pinsRef.current.get(pinKey);
    if (pinned) {
      // Same columns: put the pinned widths back on the header cells. They live
      // only on those cells, and a run that shows the spinner (WHERE, Refresh,
      // page size) unmounts the table — a new <table> under `table-layout:
      // fixed` with no widths split the space equally: every column ~35px with
      // "ui…" / "pi…" headers (owner, 06.10.2026). React also re-applies
      // className when the persisted widths go away; the pin stays.
      const widths = pinned;
      table.querySelectorAll('thead th').forEach((th, i) => {
        const w = widths[i === 0 ? ROWNUM_WIDTH_KEY : visible[i - 1]];
        if (w && th.style.width !== `${w}px`) th.style.width = `${w}px`;
      });
      table.classList.add('dbm-table--fixed');
      return;
    }
    if (visible.length === 0) return;
    const ths = table.querySelectorAll('thead th');
    // Measure the natural layout (a previous pin for other columns undone first).
    table.classList.remove('dbm-table--fixed');
    ths.forEach((th) => { th.style.width = ''; });
    const widths = measureColumnWidths(table, visible);
    ths.forEach((th, i) => {
      const w = widths[i === 0 ? ROWNUM_WIDTH_KEY : visible[i - 1]];
      if (w) th.style.width = `${w}px`;
    });
    table.classList.add('dbm-table--fixed');
    pinsRef.current.set(pinKey, widths);
    // Bounded: the oldest pins go once there are more than a session opens tabs.
    if (pinsRef.current.size > 200) pinsRef.current.delete(pinsRef.current.keys().next().value);
  }, [hasColumnWidths, loading, result, hiddenColumns, pinKey]);
  const pinnedWidths = pinsRef.current.get(pinKey) || null;
  // Colour values by column type (Settings → Extensions → DB Manager).
  const typeColors = useDbTypeColors(result?.columns, columnTypes);
  const { beginResize, guideNode } = useDbColumnResize({
    onCommit: onColumnResize || (() => {}),
    onSeed: (table) => {
      if (hasColumnWidths) return;
      // The pinned widths when there are any (the drag starts from what is on
      // screen); else measured — visible columns only, see dbColumnWidths.js.
      onColumnResize(null, pinnedWidths || measureColumnWidths(table, (result.columns || []).filter((c) => !hiddenColumns.has(c))));
    },
  });

  return {
    scrollHostRef,
    anchorCell,
    focusAnchorCell,
    virtualRows,
    windowed,
    padTop,
    padBottom,
    hasColumnWidths,
    tableElRef,
    typeColors,
    beginResize,
    guideNode,
  };
}
